/**
 * The language sheet — the «- language» variant of every main screen.
 *
 * It is not a separate screen in the file; it is a panel that drops over
 * whichever screen you were on, which is why there is a `- language` twin of
 * MAIN, 2ND MAIN, 3RD MAIN and SETTINGS rather than one picker frame. Built as
 * an overlay for the same reason, and on the shared Dialog because it is the
 * same construction as every other pop-up: a blurred wash, a gradient panel on
 * radius 70, and «Подтвердить» pinned above the footer.
 *
 * The three options are outlined pills, not bare words — «Rectangle 70/71/72»,
 * x25.1% w48.3% h3.5% on radius 24.5, the chosen one in #af4648 and the rest in
 * white. They had been built as plain text, which lost both the pills and the
 * only mark that says which language is currently on.
 *
 * The board offers three languages. Two of them have content: the catalogue is
 * bilingual and the episode scripts are Russian only. Japanese is drawn because
 * the design draws it, and says «скоро» rather than switching to a language
 * nothing is written in.
 */
import { useState } from 'react';
import { Dialog } from './Dialog.js';

type Lang = 'ru' | 'en' | 'ja';

const OPTIONS: Array<{ id: Lang; label: string; ready: boolean }> = [
  { id: 'ru', label: 'Русский', ready: true },
  { id: 'en', label: 'English', ready: true },
  { id: 'ja', label: '日本語', ready: false },
];

export function LanguagePicker({
  lang,
  onPick,
  onClose,
}: {
  lang: 'ru' | 'en';
  onPick: (next: 'ru' | 'en') => void;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<Lang>(lang);
  const [note, setNote] = useState('');

  const confirm = () => {
    if (chosen === 'ja') { setNote('скоро'); return; }
    if (chosen !== lang) onPick(chosen);
    onClose();
  };

  return (
    <Dialog
      size="language"
      label="Выберите язык"
      title="Выберите язык"
      action="Подтвердить"
      onAction={confirm}
      onClose={onClose}
      outside={note ? <p className="dlg-sub">{note}</p> : undefined}
    >
      <ul className="lang-list">
        {OPTIONS.map((o) => (
          <li key={o.id}>
            <button
              className={chosen === o.id ? 'lang-option is-on' : 'lang-option'}
              aria-pressed={chosen === o.id}
              onClick={() => { setChosen(o.id); setNote(''); }}
            >
              {o.label}
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
